import SpaceBetween from '@risk-smart/themed-cloudscape-components/space-between';
import { ParentTypes as Parent_Type_Enum } from '@risksmart-app/domain/src/types/consts/parent-type';
import type { FC } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { ownerContributorAndViewerIds } from 'src/components/form';
import { useI18NSummaryHelpContent } from 'src/components/help-panel/useSummaryHelpContent';
import LatestRatingsPreview from 'src/components/latest-ratings-preview';
import type { ResultProps } from 'src/components/latest-ratings-preview/LatestRatingsPreview';
import { ObligationChangeInformation } from 'src/components/obligation-change-information/ObligationChangeInformation';
import SourceRegulationInformation from 'src/components/source-regulation-information/SourceRegulationInformation';
import {
  useGetLatestObligationInternalAuditResultByObligationId,
  useGetLatestOversightAssessmentObligationAssessmentResultByObligationId,
} from 'src/hooks/queries';
import type { useGetObligationById } from 'src/hooks/queries/obligation/useGetObligationById';
import AssessmentResultModal from 'src/pages/assessments/modals/AssessmentResultModal';
import type { AssessmentTypeEnum } from 'src/pages/assessments/types';
import type { ObligationFormFieldData } from 'src/pages/compliance/obligations/forms/obligationSchema';
import { defaultValues } from 'src/pages/compliance/obligations/forms/obligationSchema';
import {
  getContributors,
  getOwners,
  getViewers,
} from 'src/rbac/contributorHelper';
import { useHasPermissionQuery } from 'src/rbac/useHasPermission';

import { useUpdateObligation } from '@/hooks/mutations/obligation';
import { useGetLatestObligationAssessmentResultByObligationId } from '@/hooks/queries/assessment-result/useGetLatestObligationAssessmentResultByObligationId';
import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { useIsFormPrefillEnabled } from '@/hooks/useIsFormPrefillEnabled';
import { useIsModuleEnabled } from '@/hooks/useIsModuleEnabled';

import ObligationDetailsForm from '../../../forms/ObligationDetailsForm';

type GetObligationByIdRow = NonNullable<
  ReturnType<typeof useGetObligationById>['data']
>['obligation'][number];

type Props = {
  obligation: GetObligationByIdRow;
};

const Tab: FC<Props> = ({ obligation }) => {
  useI18NSummaryHelpContent('obligations.detailsHelp');
  const {
    hasPermission: canUpdateObligation,
    loading: canUpdateObligationLoading,
  } = useHasPermissionQuery('update:obligation', obligation);
  const navigate = useNavigate();
  const { t } = useTranslation(['common']);
  const objectToolbarEnabled = useIsFeatureFlagEnabled('dynamic_forms');
  const formPrefillEnabled = useIsFormPrefillEnabled();
  const { updateObligation } = useUpdateObligation();
  const onSave = async ({
    Owners,
    Contributors,
    Viewers,
    ancestorContributors: _1,
    departments,
    tags,
    ...data
  }: ObligationFormFieldData) => {
    if (!obligation) {
      throw new Error('No obligation');
    }
    await updateObligation({
      ...data,
      Id: obligation.Id,
      // Optimistic-concurrency token: the snapshot this form was loaded from,
      // so a second editor saving over a newer row gets a 409 (COI-622).
      OriginalTimestamp: obligation.ModifiedAtTimestamp,
      ...ownerContributorAndViewerIds({ Owners, Contributors, Viewers }),
      CustomAttributeData: data.CustomAttributeData,
      DepartmentTypeIds: departments?.map((d) => d.DepartmentTypeId) || [],
      TagTypeIds: tags?.map((t) => t.TagTypeId) || [],
      schedule: data.schedule,
      Adherence: data.Adherence ?? '',
    });
  };
  const oversightEnabled = useIsModuleEnabled(
    'assessment.subModules.oversight_register_and_findings'
  );
  const internalAuditEnabled = useIsModuleEnabled('internal_audit_entity');
  const {
    hasPermission: canViewCompliance,
    loading: canViewComplianceLoading,
  } = useHasPermissionQuery('read:oversight_assessment', obligation);
  const {
    hasPermission: canViewInternalAudit,
    loading: canViewInternalAuditLoading,
  } = useHasPermissionQuery('read:internal_audit_report', obligation);
  const skipInternalAudit = !internalAuditEnabled || !canViewInternalAudit;
  const skipOversight = !oversightEnabled || !canViewCompliance;

  const { data: assessmentResult } =
    useGetLatestObligationAssessmentResultByObligationId({
      queryArgs: { obligationId: obligation.Id },
    });
  const { data: internalAuditResults } =
    useGetLatestObligationInternalAuditResultByObligationId({
      queryArgs: { obligationId: obligation.Id },
      shouldSkip: skipInternalAudit || canViewInternalAuditLoading,
    });
  const { data: oversightResults } =
    useGetLatestOversightAssessmentObligationAssessmentResultByObligationId({
      queryArgs: { obligationId: obligation.Id },
      shouldSkip: skipOversight || canViewComplianceLoading,
    });

  const [selectedAssessmentResultId, setSelectedAssessmentResultId] = useState<
    string | undefined
  >();
  const [selectedAssessmentMode, setSelectedAssessmentMode] =
    useState<AssessmentTypeEnum>('rating');
  const [showAssessmentResultModal, setShowAssessmentResultModal] =
    useState<boolean>(false);

  const onDismiss = () => navigate(-1);

  return (
    <>
      <ObligationDetailsForm
        obligationId={obligation.Id}
        parentObligationNode={obligation.parentNode}
        values={{
          ...defaultValues,
          ...obligation,
          Owners: getOwners(obligation),
          Contributors: getContributors(obligation),
          Viewers: getViewers(obligation),
          ancestorContributors: obligation?.ancestorContributors ?? [],
          schedule: obligation?.schedule ?? defaultValues.schedule,
        }}
        latestTestDate={obligation.scheduleState?.LatestDate}
        defaultValues={defaultValues}
        onSave={onSave}
        onDismiss={onDismiss}
        readOnly={!canUpdateObligation || canUpdateObligationLoading}
        publishToolbarState={objectToolbarEnabled}
        publishActiveForm={formPrefillEnabled}
        activeFormRecordId={obligation.Id}
        external={!!obligation.ExternalId}
        aside={
          <SpaceBetween size={'m'}>
            {obligation.ExternalId &&
              obligation.obligationChanges !== undefined &&
              obligation.obligationChanges.length > 0 && (
                <ObligationChangeInformation
                  changes={obligation.obligationChanges.map((change) => ({
                    id: change.Id,
                    effectiveDate: change.EffectiveDate ?? undefined,
                    sequentialId: change.SequentialId,
                  }))}
                />
              )}

            {obligation.ExternalId && (
              <SourceRegulationInformation
                id={obligation.ExternalId}
                reference={obligation.Reference}
                effectiveDate={obligation.EffectiveDate}
                regulatoryBody={obligation.regulatorySource?.RegulatorName}
              />
            )}
            {assessmentResult?.obligation_assessment_result?.[0] && (
              <LatestRatingsPreview
                ratingsTitle={t(
                  'obligationsAssessments.obligationRatingSubheading'
                )}
                onClick={(id) => {
                  setSelectedAssessmentMode('rating');
                  setSelectedAssessmentResultId(id);
                  setShowAssessmentResultModal(true);
                }}
                assessmentResults={[
                  {
                    id: assessmentResult.obligation_assessment_result?.[0]?.Id,
                    rating:
                      assessmentResult?.obligation_assessment_result?.[0]
                        ?.Rating,
                    completionDate:
                      assessmentResult?.obligation_assessment_result?.[0]
                        ?.TestDate,
                    title: 'Latest assessment rating',
                    ratingType: 'performance_result',
                  },
                ]}
              />
            )}
            {oversightResults &&
              oversightResults.obligation_oversight_result.length > 0 && (
                <LatestRatingsPreview
                  ratingsTitle={t(
                    'obligationsAssessments.oversightRatingSubheading'
                  )}
                  ratingContext={'oversight'}
                  assessmentResults={oversightResults.obligation_oversight_result.map(
                    (c) =>
                      ({
                        id: c.Id,
                        title:
                          c?.parents.length > 0
                            ? (c?.parents[0].oversightAssessment?.Title ?? '-')
                            : '-',
                        rating: c.Rating,
                        ratingType: 'performance_result',
                        completionDate: c.TestDate,
                      }) as ResultProps
                  )}
                  onClick={(id) => {
                    setSelectedAssessmentMode('oversight_assessment');
                    setSelectedAssessmentResultId(id);
                    setShowAssessmentResultModal(true);
                  }}
                />
              )}
            {internalAuditResults &&
              internalAuditResults.obligation_internal_audit_result.length >
                0 && (
                <LatestRatingsPreview
                  ratingsTitle={t('ratings.internalAuditRatingSubheading')}
                  ratingContext={'internal_audit'}
                  assessmentResults={internalAuditResults.obligation_internal_audit_result.map(
                    (c) =>
                      ({
                        id: c.Id,
                        title:
                          c?.parents?.length > 0
                            ? (c?.parents[0].internalAuditReport?.Title ?? '-')
                            : '-',
                        rating: c.Rating,
                        ratingType: 'performance_result',
                        completionDate: c.TestDate,
                      }) as ResultProps
                  )}
                  onClick={(id) => {
                    setSelectedAssessmentMode('internal_audit_report');
                    setSelectedAssessmentResultId(id);
                    setShowAssessmentResultModal(true);
                  }}
                />
              )}
          </SpaceBetween>
        }
      />
      {showAssessmentResultModal && (
        <AssessmentResultModal
          id={selectedAssessmentResultId}
          resultType={Parent_Type_Enum.ObligationAssessmentResult}
          onDismiss={() => setShowAssessmentResultModal(false)}
          i18n={t('assessmentResults')}
          assessmentMode={selectedAssessmentMode}
        />
      )}
    </>
  );
};

export default Tab;
